import Info from '@components/Info';
import { List, ListItem } from '@components/List';
import Container from '@components/UI/Container';
import Field from '@components/UI/Field';
import Icon from '@components/UI/Icon';
import Input from '@components/UI/Input';
import Link from '@components/UI/Link';
import Heading from '@components/UI/Typography/Heading';
import Paragraph from '@components/UI/Typography/Paragraph';
import supportSearchBg from '@images/supportSearchBg.svg';
import RenderIf from '@utils/renderIf';

import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { twJoin } from "tailwind-merge"

import atomsQuery from './atoms/query';
import atomsResults from './atoms/results';
import atomsVersion from './atoms/version';

const MaxLength = 99;
const MinLength = 3;

const getSupportVersion = (path) => {
    const segments = path.split('?')[0].split('/').filter(Boolean);
    return segments[0] === 'support' ? 'v2' : 'v1';
}

const SearchField = ({ isWide=false }) => {
    const router = useRouter();

    const [search, setSearch] = useAtom(atomsResults);
    const [searchQuery, setSearchQuery] = useAtom(atomsQuery);
    const [searchVersion, setSearchVersion] = useAtom(atomsVersion);
    const [showSearchModalResults, setShowSearchModalResults] = useState(false);
    
    const ref = useRef()
    const [isSearchResultsVisible, setIsSearchResultsVisible] = useState(false);

    const [debounce, setDebounce] = useState(null);
    
    const supportRoot = 'support';

    const handleChange = (event) => {
        let search = event.target.value;

        setSearchQuery(search);

        setDebounce(clearTimeout(debounce));

        if (searchQuery.length >= MinLength - 1 || router.pathname == `/${ supportRoot }/search`) {
            setDebounce(setTimeout(() => fetch(`${ process.env.NEXT_PUBLIC_APP_URL }/api/v1/support${ searchVersion === 'v2' ? '-v2' : '' }/search?q=${ search }`)
                .then(response => {
                    if(response.status !== 200) return {results: [], categories: []}
                    
                    return response.json()
                })
                .then(data => {
                    setSearch(data);
                    setShowSearchModalResults(true);
                }), 800));
        }
    };

    const handleKeyDown = (event) => {
        if((event.key === 'Backspace' && searchQuery.length <= 1) || event.key === 'Escape') {
            setShowSearchModalResults(false);
        }

        setIsSearchResultsVisible(true)

        if (searchQuery.length < MinLength) return false;

        if(event.key === 'Enter') {
            router.push({
                pathname: `/${ supportRoot }/search`,
                query: { q: searchQuery }
            });
        }
    };
    
    const handleFocus = (e) => {
        setIsSearchResultsVisible(true)
    }
    
    const handleOutsideClick = (e) => {
        if (ref.current && !ref.current.contains(e.target)) {
            setIsSearchResultsVisible(false)
        }
    }

    useEffect(() => {
        const query = router.query?.q || '';
        
        setSearchQuery(query);
    
        setSearchVersion(getSupportVersion(router.asPath));

        if (query.length >= MinLength) {
            handleChange({ target: { value: query } });
        }

        const handleRouteChange = () => {
            setShowSearchModalResults(false);
        };

        router.events.on('routeChangeStart', handleRouteChange);

        return () => {
            router.events.off('routeChangeStart', handleRouteChange);
        };
    }, [router.query?.q]);
    
    
    useEffect(() => {
        document.body.addEventListener("click", handleOutsideClick)
        
        return () => {
            document.body.removeEventListener("click", handleOutsideClick)
        }
    }, [])

    return (
        <div
            className='help-center__header'
        >
            <Container>
                <div
                    className={ `help-center__header-${ isWide ? 'wide' : 'narrow' }` }
                >
                    <RenderIf
                        isTrue={ !isWide }
                    >
                        <Heading
                            tag='h1'
                            color='white'
                        >
                            Hi! How can we help you?
                        </Heading>
                    </RenderIf>
                    
                    <Field
                        mods={ { 'no-spacing': true } }
                    >
                        <Input
                          ref={ref}
                            type='search'
                            placeholder='Write a question or problem'
                            search
                            searchIcon
                            value={ searchQuery }
                            inputProps={ {
                                maxLength: MaxLength,
                                minLength: MinLength
                            } }
                            onChange={ event => handleChange(event) }
                            onKeyDown={ event => handleKeyDown(event) }
                            onFocus={ event => handleFocus(event) }
                        />

                        <RenderIf
                            isTrue={ showSearchModalResults && search.results?.length > 0
                                && (router.pathname !== `/${ supportRoot }/search`) }
                        >
                            <div className={twJoin(
                              "help-center__search-list-wrapper",
                              isSearchResultsVisible ? "" : "hidden"
                            )}>
                                <List
                                    tag='div'
                                    mix={ { 'help-center__search-list': true } }
                                >
                                    { search.results?.length > 0 && search.results.map((search, index) => (
                                        <ListItem
                                            key={ index }
                                            href={ `/support/${ search.category.slug }/${ search.source.slug }` }
                                            action
                                        >
                                            <span
                                                dangerouslySetInnerHTML={ { __html: search.source.title } }
                                            />
                                        </ListItem>
                                    )) }
                                </List>
                            </div>
                        </RenderIf>
                    </Field>

                    <Info
                        gap={ 2 }
                        mods={ {
                            center: !isWide,
                            right: isWide,
                            row: true
                        } }
                    >
                        <RenderIf
                            isTrue={ isWide }
                        >
                            <Icon
                                name='question-rounded'
                                size={ 14 }
                                color='#ffffff'
                            />
                        </RenderIf>

                        <Paragraph
                            color='white'
                            size={ isWide ? 'sm' : 'lg' }
                            mods={ { semibold: !isWide } }
                        >
                            Didn`t find an answer?
                        </Paragraph>

                        <Link
                            href='/contact'
                            mods={ {
                                semibold: !isWide,
                                underline: true,
                                white: true,
                                sm: isWide,
                                lg: !isWide
                            } }
                        >
                            Get support
                        </Link>
                    </Info>
                </div>
            </Container>

            <div
                className={ `help-center__header-bg help-center__header-bg_${ isWide ? 'wide' : 'narrow' }` }
                style={ { backgroundImage: `url("${supportSearchBg.src}")` } }
            />
        </div>
    );
};

SearchField.displayName = 'Search-Field';

SearchField.propTypes = {
    isWide: PropTypes.bool
};

export default SearchField;
